import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../store/authStore';

export function FullPageSpinner() {
  return (
    <div className="flex h-full items-center justify-center text-mute">
      <Loader2 className="animate-spin" aria-label="Loading" />
    </div>
  );
}

/** Renders children only for signed-in users; others go to /auth and come back afterwards. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const location = useLocation();
  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'anonymous') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?mode=login&next=${next}`} replace />;
  }
  return children;
}

/** For /auth: signed-in users skip straight to the app. */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const location = useLocation();
  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'authenticated') {
    const next = new URLSearchParams(location.search).get('next');
    return (
      <Navigate to={next && next.startsWith('/') && !next.startsWith('//') ? next : '/dashboard'} replace />
    );
  }
  return children;
}
